import { useState, useEffect, useRef, useCallback } from "react";
import { createClient } from "@supabase/supabase-js";

// ── CONFIG ─────────────────────────────────────────────────────────────────
const SUPABASE_URL = "https://jojnfshtgcelxjfobsva.supabase.co";
const SUPABASE_ANON_KEY = "sb_publishable_MovdGAr8PPYbHhf-nxi7Kw_6UMWnUaP";
// Anthropic API calls are now routed through /api/claude
const WEATHER_API_KEY = "1cfd47d2e6da83d92b4752fe6040046e";
const MAPBOX_TOKEN = "pk.eyJ1IjoiemJyZXdlcjIzIiwiYSI6ImNtbWt3dmc5eTF1eG0ycHBvNGVlem1meWIifQ.dfOw0VzzR3Uv-8IsB8Bf3w";
const ELEVENLABS_API_KEY = "sk_3b092b6e62f9eef091a6c4c9a9565153208e092f4154f963";
const REVENUECAT_API_KEY = "appl_VROgDLSyBeGUJUjyElSouhByZpa";
const IAP_PRODUCT_ID = "run.velocityai.pro.monthly";

const ELEVENLABS_VOICE_ID = "jA8pwzb0q7RL7wBi3Ion";
const STRIPE_PUBLISHABLE_KEY = "pk_live_51T9tO19um3Rf1uMp1YWYP23w7S4WhzZrBrr6TUe8330NiFioBSgCHxVCBm5oZsYwPCT6LBT7eVRAbQ6Zzgm2nAZD00ufJWxtwX";

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// ── POSTHOG ───────────────────────────────────────────────────────────────
const POSTHOG_KEY = "phc_GBxD670brRwLreW5DXr9NXplWQj4BX3IwnuBVA4Sqez";
const POSTHOG_HOST = "https://us.i.posthog.com";

function initPostHog() {
  if (window.posthog) return;
  !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]);t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+" (stub)"},o="capture identify alias people.set people.set_once set register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures getActiveMatchingSurveys getSurveys getNextSurveyStep onSessionId setPersonPropertiesForFlags".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
  window.posthog.init(POSTHOG_KEY, { api_host: POSTHOG_HOST, autocapture: true, capture_heatmaps: true });
}

function track(event, props) {
  if (window.posthog) window.posthog.capture(event, props);
}

// Global route store — persists across React renders and tab switches
let GLOBAL_SUGGESTED_ROUTE = null;

// ── HELPERS ────────────────────────────────────────────────────────────────
function haversineDistance(a, b) {
  const R = 3958.8;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) *
      Math.cos((b.lat * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

function formatPace(minPerMile) {
  if (!isFinite(minPerMile) || minPerMile > 60) return "--:--";
  const m = Math.floor(minPerMile);
  const s = Math.round((minPerMile - m) * 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatTime(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// Persistent audio element — unlocked on first user tap
const _audio = new Audio();
let _audioCtx = null;
let _gainNode = null;

async function unlockAudio() {
  try {
    _audio.src = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAA" +
      "EAAQARKwAAESsAAAEACABkYXRhAAAAAA==";
    await _audio.play();

    // Set up Web Audio context for ducking
    if (!_audioCtx) {
      _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      _gainNode = _audioCtx.createGain();
      _gainNode.connect(_audioCtx.destination);
    }
  } catch (_) {}
}

function duckAudio(duck) {
  // On iOS, playing audio automatically ducks background music
  // We also use the mediaSession API to signal audio focus
  try {
    if ("mediaSession" in navigator) {
      if (duck) {
        // Signal that we're playing important audio
        navigator.mediaSession.playbackState = "playing";
      } else {
        navigator.mediaSession.playbackState = "paused";
      }
    }
  } catch (_) {}
}

async function speak(text, forceSpeak = false) {
  // Check quiet mode via DOM (speak is outside React)
  const isQuiet = document.getElementById("quiet-mode-store")?.dataset?.quiet === "true";
  if (isQuiet && !forceSpeak) {
    return;
  }
  // Check voice message limit via DOM
  const voiceUsed = parseInt(document.getElementById("voice-count-store")?.dataset?.count || "0");
  const voiceLimit = parseInt(document.getElementById("voice-count-store")?.dataset?.limit || "999");
  const isRunning = document.getElementById("voice-count-store")?.dataset?.running === "true";
  if (isRunning && !forceSpeak && voiceUsed >= voiceLimit) {
    // Limit reached — silent coaching only
    return;
  }
  // Increment voice count
  const store = document.getElementById("voice-count-store");
  if (store && isRunning && !forceSpeak) {
    store.dataset.count = String(voiceUsed + 1);
  }
  try {
    // Duck background music before speaking
    duckAudio(true);

    const res = await fetch(
      `https://api.elevenlabs.io/v1/text-to-speech/${ELEVENLABS_VOICE_ID}/stream`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "xi-api-key": ELEVENLABS_API_KEY,
        },
        body: JSON.stringify({
          text,
          model_id: "eleven_turbo_v2",
          voice_settings: { stability: 0.5, similarity_boost: 0.75, style: 0.4, use_speaker_boost: true },
        }),
      }
    );
    if (!res.ok) throw new Error("ElevenLabs error: " + res.status);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    _audio.src = url;

    // When audio ends, restore background music
    _audio.onended = () => {
      URL.revokeObjectURL(url);
      duckAudio(false);
    };

    // Set audio category for iOS to allow ducking
    _audio.setAttribute("x-webkit-airplay", "allow");

    await _audio.play();
  } catch (e) {
    duckAudio(false);
    console.error("ElevenLabs failed, falling back to browser TTS:", e);
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 0.95;
      u.pitch = 1.05;
      window.speechSynthesis.speak(u);
      u.onend = () => duckAudio(false);
    }
  }
}

// ── MAPBOX ROUTE MAP ──────────────────────────────────────────────────────────
function RouteMap({ points, suggestedRoute, onMapReady }) {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    if (mapRef.current || !mapContainer.current) return;

    if (!document.getElementById("mapbox-css")) {
      const link = document.createElement("link");
      link.id = "mapbox-css";
      link.rel = "stylesheet";
      link.href = "https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.css";
      document.head.appendChild(link);
    }

    const initMap = () => {
      if (mapRef.current || !mapContainer.current) return;
      window.mapboxgl.accessToken = MAPBOX_TOKEN;
      const center = points.length > 0
        ? [points[points.length - 1].lon, points[points.length - 1].lat]
        : [-80.1918, 25.7617];

      mapRef.current = new window.mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/mapbox/satellite-streets-v12",
        center,
        zoom: 15,
        attributionControl: false,
      });

      mapRef.current.on("load", () => {
        // GPS trace layer (green)
        mapRef.current.addSource("route", {
          type: "geojson",
          data: { type: "Feature", geometry: { type: "LineString", coordinates: [] } },
        });
        mapRef.current.addLayer({
          id: "route",
          type: "line",
          source: "route",
          paint: { "line-color": "#00ff88", "line-width": 4, "line-opacity": 0.9 },
        });
        // Suggested route layer (blue dashed)
        mapRef.current.addSource("suggested", {
          type: "geojson",
          data: { type: "Feature", geometry: { type: "LineString", coordinates: [] } },
        });
        mapRef.current.addLayer({
          id: "suggested",
          type: "line",
          source: "suggested",
          paint: { "line-color": "#4488ff", "line-width": 3, "line-opacity": 0.8, "line-dasharray": [2, 2] },
        });
        // Draw suggested route immediately if one exists globally
        if (GLOBAL_SUGGESTED_ROUTE && GLOBAL_SUGGESTED_ROUTE.length > 0) {
          const src = mapRef.current.getSource("suggested");
          if (src) {
            src.setData({ type: "Feature", geometry: { type: "LineString", coordinates: GLOBAL_SUGGESTED_ROUTE } });
            const lons = GLOBAL_SUGGESTED_ROUTE.map(c => c[0]);
            const lats = GLOBAL_SUGGESTED_ROUTE.map(c => c[1]);
            mapRef.current.fitBounds(
              [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]],
              { padding: 40, duration: 600 }
            );
          }
        }
        setMapLoaded(true);
        if (onMapReady) onMapReady(mapRef.current);
      });
    };

    if (!document.getElementById("mapbox-js")) {
      const script = document.createElement("script");
      script.id = "mapbox-js";
      script.src = "https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.js";
      script.onload = initMap;
      document.head.appendChild(script);
    } else if (window.mapboxgl) {
      initMap();
    }
  }, []);

  // Update GPS trace
  useEffect(() => {
    if (!mapRef.current || !mapLoaded || points.length < 2) return;
    const coords = points.map((p) => [p.lon, p.lat]);
    const source = mapRef.current.getSource("route");
    if (source) source.setData({ type: "Feature", geometry: { type: "LineString", coordinates: coords } });
    const last = points[points.length - 1];
    mapRef.current.easeTo({ center: [last.lon, last.lat], duration: 500 });
  }, [points, mapLoaded]);

  // Update suggested route
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;
    const source = mapRef.current.getSource("suggested");
    if (!source) return;
    if (suggestedRoute && suggestedRoute.length > 0) {
      source.setData({ type: "Feature", geometry: { type: "LineString", coordinates: suggestedRoute } });
      const lons = suggestedRoute.map(c => c[0]);
      const lats = suggestedRoute.map(c => c[1]);
      mapRef.current.fitBounds(
        [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]],
        { padding: 40, duration: 800 }
      );
    } else {
      source.setData({ type: "Feature", geometry: { type: "LineString", coordinates: [] } });
    }
  }, [suggestedRoute, mapLoaded]);

  return (
    <div style={{ position: "relative", borderRadius: 12, overflow: "hidden", height: 220 }}>
      <div ref={mapContainer} style={{ width: "100%", height: "100%" }} />
      {points.length === 0 && !suggestedRoute && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.6)", color: "#666", fontSize: 13, pointerEvents: "none" }}>
          Map will track your route as you run
        </div>
      )}
    </div>
  );
}

// ── WEATHER WIDGET ─────────────────────────────────────────────────────────
function WeatherBar({ weather }) {
  if (!weather) return null;
  return (
    <div style={{ background: "#1a2a1a", border: "1px solid #2a4a2a", borderRadius: 10, padding: "10px 16px", display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap", fontSize: 13, color: "#88bb88" }}>
      <span>🌡 {Math.round(weather.temp)}°F</span>
      <span>💧 {weather.humidity}% humidity</span>
      <span>💨 {Math.round(weather.windSpeed)} mph wind</span>
      <span style={{ color: "#aaddaa" }}>☁ {weather.description}</span>
      {weather.feelsLike && <span>Feels {Math.round(weather.feelsLike)}°F</span>}
    </div>
  );
}

// ── MAIN APP ───────────────────────────────────────────────────────────────
export default function PaceAI() {
  const [user, setUser] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [hrDevice, setHrDevice] = useState(null);
  const [coachingInterval, setCoachingInterval] = useState(300);
  const [manualMode, setManualMode] = useState(false);
  const [quietMode, setQuietMode] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [runnerQuestion, setRunnerQuestion] = useState("");
  const [coachReply, setCoachReply] = useState("");
  const [replyLoading, setReplyLoading] = useState(false);
  const [runRating, setRunRating] = useState(0);
  const [voiceMessagesUsed, setVoiceMessagesUsed] = useState(0);
  const VOICE_MESSAGE_LIMIT = 10;
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [hrConnecting, setHrConnecting] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [trainingPlan, setTrainingPlan] = useState(null);
  const [showShareCard, setShowShareCard] = useState(false);
  const [routeGoal, setRouteGoal] = useState("3");
  const [routeOptions, setRouteOptions] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [suggestedRouteCoords, setSuggestedRouteCoords] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [shareCardData, setShareCardData] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [tab, setTab] = useState("run");
  const [running, setRunning] = useState(false);
  const [paused, setPaused] = useState(false);
  const [gpsReady, setGpsReady] = useState(false);
  const [gpsAccuracy, setGpsAccuracy] = useState(null);
  const [gpsPoints, setGpsPoints] = useState([]);
  const [distance, setDistance] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [currentPace, setCurrentPace] = useState(0);
  const [avgPace, setAvgPace] = useState(0);
  const [heartRate, setHeartRate] = useState(null);
  const [cadence, setCadence] = useState(null);
  const [calories, setCalories] = useState(0);
  const [coachMessage, setCoachMessage] = useState("Getting GPS ready...");
  const [coachLoading, setCoachLoading] = useState(false);
  const [weather, setWeather] = useState(null);
  const [toast, setToast] = useState(null);
  const [runHistory, setRunHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [debrief, setDebrief] = useState(null);
  const [debriefLoading, setDebriefLoading] = useState(false);
  const [lastMilestone, setLastMilestone] = useState(0);

  const watchRef = useRef(null);
  const timerRef = useRef(null);
  const coachTimerRef = useRef(null);
  const lastPointRef = useRef(null);
  const startTimeRef = useRef(null);
  const sharedMapRef = useRef(null);

  // ── AUTH ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    initPostHog();
    // Request push notification permission
    try {
      const isNative = window.Capacitor?.isNativePlatform?.() || window.cordova;
      if (isNative && window.Capacitor?.Plugins?.PushNotifications) {
        const { PushNotifications } = window.Capacitor.Plugins;
        if (typeof PushNotifications.addListener === "function") {
          PushNotifications.addListener("registration", (token) => {
            supabase.from("push_tokens").upsert({ user_id: "pending", token: token.value, platform: "ios" }).catch(() => {});
          });
        }
        if (typeof PushNotifications.requestPermissions === "function") {
          PushNotifications.requestPermissions().then(result => {
            if (result.receive === "granted" && typeof PushNotifications.register === "function") {
              PushNotifications.register();
            }
          }).catch((e) => console.log("Push permission error:", e));
        }
      } else if ("Notification" in window && !isNative) {
        // Web fallback
        Notification.requestPermission().catch(() => {});
      }
    } catch(e) {
      console.log("Push notification init:", e);
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const u = session?.user ?? null;
      setUser(u);
      setAuthLoading(false);
      if (u) {
        checkSubscription(u.id);
        if (window.posthog) window.posthog.identify(u.id, { email: u.email, name: u.user_metadata?.full_name });
        if (!localStorage.getItem("paceai_onboarded")) {
          setShowOnboarding(true);
        }

      }
    });
    // Handle return from Stripe
    const params = new URLSearchParams(window.location.search);
    if (params.get("subscribed") === "true") {
      setIsPro(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const u = session?.user ?? null;
      setUser(u);
      if (u) {
        checkSubscription(u.id);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const onboardingSteps = [
    {
      emoji: "⚡",
      title: "Welcome to Velocity AI",
      desc: "Your AI running coach that talks to you mid-run. It knows your pace, the weather, and how fatigued you are — and gives you real coaching every 90 seconds.",
      btn: "Let's Go →"
    },
    {
      emoji: "📍",
      title: "Enable GPS First",
      desc: "Velocity AI uses your phone's GPS to track your route and calculate your real pace. Tap 'Enable GPS' on the Run tab and allow location access when prompted.",
      btn: "Got It →"
    },
    {
      emoji: "🏃",
      title: "You're Ready to Run",
      desc: "Hit START RUN when you're ready. Your AI coach will speak to you through your earbuds. After your run you'll get a personal debrief and training plan.",
      btn: "Start Running"
    },
  ];

  function completeOnboarding() {
    localStorage.setItem("paceai_onboarded", "true");
    setShowOnboarding(false);
    track("onboarding_completed");
  }

  async function captureEmail(e) {
    e.preventDefault();
    if (!emailInput || !emailInput.includes("@")) return;
    setEmailLoading(true);
    try {
      await supabase.from("email_captures").upsert({ email: emailInput.toLowerCase().trim(), source: "landing_page" });
      setEmailSubmitted(true);
      track("email_captured", { source: "landing_page" });
    } catch (err) {
      console.error("Email capture error:", err);
    } finally {
      setEmailLoading(false);
    }
  }

  async function handleNativeOAuth(provider) {
    try {
      const Browser = window.Capacitor?.Plugins?.Browser;
      const CapApp = window.Capacitor?.Plugins?.App;

      if (!Browser || typeof Browser.open !== "function") {
        showToast("Browser plugin not available.");
        return;
      }

      const { data } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: "com.velocityai.app://login-callback",
          skipBrowserRedirect: true,
        },
      });

      if (!data?.url) {
        showToast("Sign-in failed. Try again.");
        return;
      }

      // Listen for the app URL callback after OAuth completes
      let listener = null;
      if (CapApp && typeof CapApp.addListener === "function") {
        listener = await CapApp.addListener("appUrlOpen", async (event) => {
          if (event.url.startsWith("com.velocityai.app://login-callback")) {
            if (listener) await listener.remove();
            if (typeof Browser.close === "function") await Browser.close();
            const url = new URL(event.url.replace("com.velocityai.app://login-callback", "https://velocityai.run"));
            const hashParams = new URLSearchParams(url.hash.replace("#", ""));
            const accessToken = hashParams.get("access_token");
            const refreshToken = hashParams.get("refresh_token");
            if (accessToken && refreshToken) {
              const { data: sessionData } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken,
              });
              if (sessionData?.user) {
                setUser(sessionData.user);
                checkSubscription(sessionData.user.id);
              }
            } else {
              const { data: { session } } = await supabase.auth.getSession();
              if (session?.user) {
                setUser(session.user);
                checkSubscription(session.user.id);
              }
            }
          }
        });
      }

      await Browser.open({ url: data.url });
    } catch (e) {
      console.error("Native OAuth error:", e);
      showToast("Sign-in failed. Try again.");
    }
  }

  async function signInWithGoogle() {
    const isNative = window.Capacitor?.isNativePlatform?.();
    if (isNative) {
      await handleNativeOAuth("google");
    } else {
      await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo: window.location.origin },
      });
    }
  }

  async function signInWithApple() {
    const isNative = window.Capacitor?.isNativePlatform?.();
    if (isNative) {
      try {
        // Use our custom native Swift plugin registered as "AppleSignIn"
        const AppleSignIn = window.Capacitor?.Plugins?.AppleSignIn;
        console.log("AppleSignIn plugin found:", typeof AppleSignIn?.authorize);

        if (AppleSignIn && typeof AppleSignIn.authorize === "function") {
          // Generate nonce — must be passed to both Apple and Supabase
          const rawNonce = Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2);
          
          console.log("Apple authorize starting with native Swift plugin...");
          const result = await AppleSignIn.authorize({
            nonce: rawNonce,
          });
          
          console.log("Apple authorize result:", JSON.stringify(result));
          const identityToken = result?.response?.identityToken;
          console.log("Identity token present:", !!identityToken);
          
          if (!identityToken) {
            showToast("Sign in failed — no token.");
            return;
          }
          
          console.log("Calling Supabase signInWithIdToken...");
          const { data, error } = await supabase.auth.signInWithIdToken({
            provider: "apple",
            token: identityToken,
            nonce: rawNonce,
          });
          
          console.log("Supabase result - data:", JSON.stringify(data?.user?.id), "error:", JSON.stringify(error));
          
          if (error) {
            console.error("Supabase Apple sign in error:", error);
            showToast("Sign in error: " + error.message);
            return;
          }
          
          if (data?.user) {
            console.log("User set:", data.user.id);
            setUser(data.user);
            await checkSubscription(data.user.id);
            showToast("Welcome! 🎉");
          } else {
            console.log("No user in data, trying getSession...");
            // Try getting session directly as fallback
            const { data: { session } } = await supabase.auth.getSession();
            console.log("Session user:", session?.user?.id);
            if (session?.user) {
              setUser(session.user);
              await checkSubscription(session.user.id);
              showToast("Welcome! 🎉");
            } else {
              showToast("Sign in failed. Try again.");
            }
          }
        } else {
          console.error("AppleSignIn native plugin not found, falling back to OAuth");
          await handleNativeOAuth("apple");
        }
      } catch (e) {
        if (e?.code !== "1001") {
          console.error("Apple sign in error:", e);
          showToast("Sign in failed. Try again.");
        }
      }
    } else {
      await supabase.auth.signInWithOAuth({
        provider: "apple",
        options: { redirectTo: window.location.origin },
      });
    }
  }

  async function signOut() {
    await supabase.auth.signOut();
    setIsPro(false);
  }

  async function initRevenueCat() {
    try {
      const { Purchases } = window.Capacitor.Plugins;
      if (!Purchases) return;
      await Purchases.configure({ apiKey: REVENUECAT_API_KEY });
    } catch (e) {
      console.error("RevenueCat init error:", e);
    }
  }

  async function checkRevenueCatSubscription() {
    try {
      const { Purchases } = window.Capacitor.Plugins;
      if (!Purchases) return;
      const { customerInfo } = await Purchases.getCustomerInfo();
      const entitlements = customerInfo?.entitlements?.active;
      if (entitlements && entitlements["Velocity AI Pro"]) {
        setIsPro(true);
      }
    } catch (e) {
      console.error("RevenueCat check error:", e);
    }
  }

  async function purchaseProNative() {
    try {
      const { Purchases } = window.Capacitor.Plugins;
      if (!Purchases) {
        showToast("In-app purchase not available.");
        return;
      }
      // Get offerings with retry logic
      let pkg = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        const offeringsResult = await Purchases.getOfferings();
        console.log("Offerings attempt", attempt, "keys:", JSON.stringify(Object.keys(offeringsResult || {})));
        
        pkg = offeringsResult?.current?.monthly 
          || offeringsResult?.offerings?.current?.monthly
          || offeringsResult?.current?.availablePackages?.[0]
          || offeringsResult?.all?.default?.monthly
          || offeringsResult?.all?.default?.availablePackages?.[0];
        
        console.log("Package found:", !!pkg);
        if (pkg) break;
        
        // Wait 1 second before retrying
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      if (!pkg) {
        showToast("Product not found. Try again.");
        return;
      }
      const purchaseResult = await Purchases.purchasePackage({ aPackage: pkg });
      console.log("Purchase result:", JSON.stringify(purchaseResult).substring(0, 200));
      const customerInfo = purchaseResult?.customerInfo || purchaseResult;
      const entitlements = customerInfo?.entitlements?.active;
      if (entitlements && entitlements["Velocity AI Pro"]) {
        setIsPro(true);
        showToast("Welcome to Pro! 🎉");
        if (user?.id) {
          await supabase.from("subscriptions").upsert({
            user_id: user.id,
            status: "active",
            provider: "apple",
          });
        }
      } else {
        showToast("Purchase completed! Restart app if Pro doesn't activate.");
      }
    } catch (e) {
      if (e?.code !== "1") {
        showToast("Purchase failed. Please try again.");
      }
      console.error("Purchase error:", e);
    }
  }

  async function signInAndPurchase() {
    // Sign in with Apple first, then purchase
    try {
      await signInWithApple();
      // Wait for auth state and RevenueCat to fully initialize
      await new Promise(resolve => setTimeout(resolve, 4000));
      // Now trigger purchase
      await purchaseProNative();
    } catch (e) {
      console.error("Sign in and purchase error:", e);
      showToast("Sign in first, then tap Upgrade Now.");
    }
  }

  async function restorePurchases() {
    try {
      const { Purchases } = window.Capacitor.Plugins;
      if (!Purchases) return;
      const { customerInfo } = await Purchases.restorePurchases();
      const entitlements = customerInfo?.entitlements?.active;
      if (entitlements && entitlements["Velocity AI Pro"]) {
        setIsPro(true);
        showToast("Pro access restored! 🎉");
      } else {
        showToast("No previous purchase found.");
      }
    } catch (e) {
      showToast("Restore failed. Please try again.");
      console.error("Restore error:", e);
    }
  }

  async function checkSubscription(userId) {
    const { data } = await supabase
      .from("subscriptions")
      .select("status")
      .eq("user_id", userId)
      .maybeSingle();
    if (data?.status === "active") setIsPro(true);
    // Also check RevenueCat for native IAP subscribers
    const isNative = window.Capacitor?.isNativePlatform?.();
    if (isNative) {
      await initRevenueCat();
      await checkRevenueCatSubscription();
    }
  }

  function isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  }

  async function connectHRMonitor() {
    if (isIOS()) {
      showToast("HR monitors require Android Chrome. iOS coming soon!");
      return;
    }
    if (!navigator.bluetooth) {
      showToast("Web Bluetooth not supported on this browser. Use Chrome on Android.");
      return;
    }
    setHrConnecting(true);
    try {
      const device = await navigator.bluetooth.requestDevice({
        filters: [{ services: ["heart_rate"] }],
        optionalServices: ["heart_rate"],
      });
      const server = await device.gatt.connect();
      const service = await server.getPrimaryService("heart_rate");
      const characteristic = await service.getCharacteristic("heart_rate_measurement");

      await characteristic.startNotifications();
      characteristic.addEventListener("characteristicvaluechanged", (event) => {
        const value = event.target.value;
        const flags = value.getUint8(0);
        const rate16Bits = flags & 0x1;
        const hr = rate16Bits ? value.getUint16(1, true) : value.getUint8(1);
        setHeartRate(hr);
      });

      setHrDevice(device);
      showToast(`Connected to ${device.name}! ❤️`);
      track("hr_monitor_connected");

      device.addEventListener("gattserverdisconnected", () => {
        setHrDevice(null);
        setHeartRate(null);
        showToast("HR monitor disconnected.");
      });
    } catch (e) {
      if (e.name !== "NotFoundError") {
        showToast("HR monitor connection failed.");
      }
    } finally {
      setHrConnecting(false);
    }
  }

  function disconnectHRMonitor() {
    if (hrDevice?.gatt?.connected) {
      hrDevice.gatt.disconnect();
    }
    setHrDevice(null);
    setHeartRate(null);
    showToast("HR monitor disconnected.");
  }





  async function askCoach() {
    if (!("webkitSpeechRecognition" in window) && !("SpeechRecognition" in window)) {
      showToast("Voice recognition not supported on this browser.");
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    setIsListening(true);
    setRunnerQuestion("");
    setCoachReply("");

    recognition.onresult = async (event) => {
      const question = event.results[0][0].transcript;
      setRunnerQuestion(question);
      setIsListening(false);
      setReplyLoading(true);

      try {
        const fatigue = distance > 3 ? "high" : distance > 1.5 ? "moderate" : "low";
        const weatherContext = weather
          ? `Weather: ${Math.round(weather.temp)}°F, ${weather.description}, wind ${Math.round(weather.windSpeed)} mph.`
          : "";
        const hrContext = heartRate ? `Heart rate: ${heartRate} bpm.` : "";

        const prompt = `You are a running coach mid-run. The runner just asked you a question.

Current run stats:
- Distance: ${distance.toFixed(2)} miles
- Current pace: ${formatPace(currentPace)} min/mile
- Time running: ${formatTime(elapsed)}
- Fatigue: ${fatigue}
- ${hrContext}
- ${weatherContext}

Runner's question: "${question}"

Answer in 2-3 sentences max. Be direct, specific, and encouraging. Use their actual stats in your answer.`;

        const res = await fetch("/api/claude", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            model: "claude-haiku-4-5-20251001",
            max_tokens: 150,
            messages: [{ role: "user", content: prompt }],
          }),
        });

        const data = await res.json();
        const reply = data.content?.[0]?.text || "Keep going — you're doing great!";
        setCoachReply(reply);
        speak(reply);
        track("coach_question_asked");
      } catch (e) {
        setCoachReply("Keep pushing — you're doing great!");
      } finally {
        setReplyLoading(false);
      }
    };

    recognition.onerror = (event) => {
      setIsListening(false);
      if (event.error !== "aborted") showToast("Couldn't hear you — try again.");
    };

    recognition.onend = () => setIsListening(false);
    recognition.start();
  }

  async function cancelSubscription() {
    if (!window.confirm("Cancel your Pro subscription? You'll lose access to AI coaching, training plans, and route intelligence.")) return;
    try {
      const res = await fetch("/api/cancel-subscription", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.id }),
      });
      const data = await res.json();
      if (data.success) {
        setIsPro(false);
        showToast("Subscription canceled.");
        track("subscription_canceled");
      } else {
        showToast("Cancel failed — contact support.");
      }
    } catch (e) {
      showToast("Cancel failed — try again.");
    }
  }

  async function startCheckout() {
    const isNative = window.Capacitor?.isNativePlatform?.();
    if (isNative) {
      await purchaseProNative();
    } else {
      try {
        const res = await fetch("/api/create-checkout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: user.id,
            userEmail: user.email,
          }),
        });
        const { url } = await res.json();
        window.location.href = url;
      } catch (e) {
        console.error("Checkout error:", e);
        showToast("Checkout failed — try again.");
      }
    }
  }

  // ── GPS SETUP ────────────────────────────────────────────────────────────
  const enableGPS = useCallback(async () => {
    console.log("enableGPS called, isNative:", !!window.Capacitor?.isNativePlatform?.());
    setCoachMessage("Requesting GPS permission...");
    
    try {
      // Use Capacitor Geolocation plugin on native iOS
      const isNative = window.Capacitor?.isNativePlatform?.();
      if (isNative && window.Capacitor?.Plugins?.Geolocation) {
        const { Geolocation } = window.Capacitor.Plugins;
        console.log("Using Capacitor Geolocation plugin");
        
        // Request permission first
        const perm = await Geolocation.requestPermissions();
        console.log("GPS permission:", JSON.stringify(perm));
        
        if (perm.location === "denied") {
          setCoachMessage("GPS permission denied. Enable in Settings.");
          return;
        }
        
        const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 15000 });
        console.log("GPS success:", pos.coords.latitude, pos.coords.longitude);
        setGpsReady(true);
        setGpsAccuracy(Math.round(pos.coords.accuracy));
        setCurrentLocation({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        setCoachMessage("GPS locked. Tap START to begin your run.");
        fetchWeather(pos.coords.latitude, pos.coords.longitude);
      } else {
        // Web fallback
        if (!navigator.geolocation) {
          setCoachMessage("GPS not supported on this device.");
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            console.log("GPS success (web):", pos.coords.latitude, pos.coords.longitude);
            setGpsReady(true);
            setGpsAccuracy(Math.round(pos.coords.accuracy));
            setCurrentLocation({ lat: pos.coords.latitude, lon: pos.coords.longitude });
            setCoachMessage("GPS locked. Tap START to begin your run.");
            fetchWeather(pos.coords.latitude, pos.coords.longitude);
          },
          (err) => {
            console.error("GPS error:", err.code, err.message);
            setCoachMessage("GPS error: " + err.message);
          },
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
        );
      }
    } catch (err) {
      console.error("GPS exception:", err);
      setCoachMessage("GPS error: " + err.message);
    }
  }, []);

  // ── WEATHER ──────────────────────────────────────────────────────────────
  async function fetchWeather(lat, lon) {
    try {
      const res = await fetch(
        `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&appid=${WEATHER_API_KEY}&units=imperial`,
        { mode: "cors" }
      );
      if (!res.ok) {
        console.warn("Weather API returned:", res.status);
        return;
      }
      const data = await res.json();
      if (!data.main) {
        console.warn("Weather API unexpected response:", data);
        return;
      }
      setWeather({
        temp: data.main.temp,
        feelsLike: data.main.feels_like,
        humidity: data.main.humidity,
        windSpeed: data.wind?.speed ?? 0,
        description: data.weather?.[0]?.description ?? "",
        main: data.weather?.[0]?.main ?? "",
      });
    } catch (e) {
      console.error("Weather fetch failed:", e);
      // App continues fine without weather — AI coach will just omit weather context
    }
  }

  // ── AI COACH ────────────────────────────────────────────────────────────
  const fetchCoachMessage = useCallback(async (dist, pace, elapsedSec, weatherData) => {
    if (coachLoading) return;
    setCoachLoading(true);
    try {
      const fatigue = dist > 3 ? "high" : dist > 1.5 ? "moderate" : "low";
      const weatherContext = weatherData
        ? `Weather: ${Math.round(weatherData.temp)}°F, feels like ${Math.round(weatherData.feelsLike)}°F, ${weatherData.humidity}% humidity, wind ${Math.round(weatherData.windSpeed)} mph, ${weatherData.description}.`
        : "Weather data unavailable.";

      // Get current HR from state via ref workaround
      const currentHR = document.getElementById("hr-data-store")?.dataset?.hr;
      const hrContext = currentHR ? `Heart rate: ${currentHR} bpm` : "Heart rate: not available";

      const prompt = `You are an encouraging, data-driven running coach giving a real-time mid-run message.

Runner stats right now:
- Distance: ${dist.toFixed(2)} miles
- Current pace: ${formatPace(pace)} min/mile
- Time running: ${formatTime(elapsedSec)}
- Fatigue level: ${fatigue}
- ${hrContext}
- ${weatherContext}

Give ONE short coaching message (2-3 sentences max). Be specific to their current stats. Be motivating but honest. If it's hot or humid, address it. If pace is strong, acknowledge it. If they're slowing, encourage them to pick it up. Sound like a real coach, not a robot.`;

      const res = await fetch("/api/claude", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: 150,
          messages: [{ role: "user", content: prompt }],
        }),
      });
      const data = await res.json();
      const msg = data.content?.[0]?.text || "Keep pushing — you're doing great!";
      setCoachMessage(msg);
      speak(msg);
    } catch (e) {
      console.error("Coach error:", e);
      setCoachMessage("Keep going — you're crushing it!");
    } finally {
      setCoachLoading(false);
    }
  }, [coachLoading]);

  // ── POST-RUN DEBRIEF ─────────────────────────────────────────────────────
  async function fetchDebrief(dist, avgPaceFinal, elapsedSec, weatherData) {
    setDebriefLoading(true);
    try {
      const weatherContext = weatherData
        ? `Conditions: ${Math.round(weatherData.temp)}°F, ${weatherData.humidity}% humidity, wind ${Math.round(weatherData.windSpeed)} mph.`
        : "";

      const prompt = `You are a running coach giving a post-run analysis.

Run summary:
- Distance: ${dist.toFixed(2)} miles
- Average pace: ${formatPace(avgPaceFinal)} min/mile
- Total time: ${formatTime(elapsedSec)}
- Estimated calories: ${Math.round(dist * 100)}
- ${weatherContext}

Write a personalized post-run debrief in 3-5 sentences. Cover: what went well, one thing to improve, and one specific tip for their next run. Be conversational and encouraging, like a coach debriefing after practice.`;

      const res = await fetch("/api/claude", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: 250,
          messages: [{ role: "user", content: prompt }],
        }),
      });
      const data = await res.json();
      setDebrief(data.content?.[0]?.text || "Great run today! Keep up the consistency.");
    } catch (e) {
      setDebrief("Great run! Keep building that consistency.");
    } finally {
      setDebriefLoading(false);
    }
  }

  // ── START RUN ────────────────────────────────────────────────────────────
  function startRun() {
    unlockAudio();
    track("run_started", { isPro });
    setRunning(true);
    setRunRating(0);
    setRatingSubmitted(false);
    setVoiceMessagesUsed(0);
    setPaused(false);
    setGpsPoints([]);
    setDistance(0);
    setElapsed(0);
    setCalories(0);
    setLastMilestone(0);
    setDebrief(null);
    lastPointRef.current = null;
    startTimeRef.current = Date.now();

    setCoachMessage("Let's go! Run strong.");
    speak("Let's go! Run strong.");

    // GPS watch — use Capacitor Geolocation on native
    const isNative = window.Capacitor?.isNativePlatform?.();
    const CapGeo = window.Capacitor?.Plugins?.Geolocation;
    if (isNative && CapGeo) {
      watchRef.current = CapGeo.watchPosition({ enableHighAccuracy: true, timeout: 5000 }, (pos, err) => {
        if (err) { console.error("GPS watch error:", err); return; }
        if (!pos) return;
        const newPoint = { lat: pos.coords.latitude, lon: pos.coords.longitude, ts: Date.now() };
        setGpsAccuracy(Math.round(pos.coords.accuracy));
        if (pos.coords.accuracy > 50) return;
        setGpsPoints((prev) => {
          const updated = [...prev, newPoint];
          if (lastPointRef.current) {
            const added = haversineDistance(lastPointRef.current, newPoint);
            if (added < 0.1) {
              setDistance((d) => {
                const newDist = d + added;
                setLastMilestone((lm) => {
                  const milestones = [0.5, 1, 2, 3, 5];
                  for (const m of milestones) {
                    if (lm < m && newDist >= m) {
                      const msg = m === 0.5 ? "Half mile done!" : `${m} mile${m > 1 ? "s" : ""}! Keep going!`;
                      showToast(msg);
                      speak(msg);
                      return m;
                    }
                  }
                  return lm;
                });
                return newDist;
              });
            }
          }
          lastPointRef.current = newPoint;
          return updated;
        });
      });
    } else {
      watchRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          const newPoint = { lat: pos.coords.latitude, lon: pos.coords.longitude, ts: Date.now() };
          setGpsAccuracy(Math.round(pos.coords.accuracy));
          if (pos.coords.accuracy > 50) return;
          setGpsPoints((prev) => {
            const updated = [...prev, newPoint];
            if (lastPointRef.current) {
              const added = haversineDistance(lastPointRef.current, newPoint);
              if (added < 0.1) {
                setDistance((d) => {
                  const newDist = d + added;
                  setLastMilestone((lm) => {
                    const milestones = [0.5, 1, 2, 3, 5];
                    for (const m of milestones) {
                      if (lm < m && newDist >= m) {
                        const msg = m === 0.5 ? "Half mile done!" : `${m} mile${m > 1 ? "s" : ""}! Keep going!`;
                        showToast(msg);
                        speak(msg);
                        return m;
                      }
                    }
                    return lm;
                  });
                  return newDist;
                });
              }
            }
            lastPointRef.current = newPoint;
            return updated;
          });
        },
        (err) => console.error("GPS watch error:", err),
        { enableHighAccuracy: true, maximumAge: 2000, timeout: 5000 }
      );
    }

    // Timer
    console.log("Starting timer interval");
    timerRef.current = setInterval(() => {
      console.log("Timer tick");
      setElapsed((e) => {
        const newE = e + 1;
        setDistance((d) => {
          if (d > 0 && newE > 0) {
            const pace = newE / 60 / d;
            setCurrentPace(pace);
            setAvgPace(pace);
            setCalories(Math.round(d * 100));
          }
          return d;
        });
        return newE;
      });
    }, 1000);

    // AI coach timer — Pro gets full coaching, free gets text-only every 10 min
    if (!manualMode || isPro) {
      const intervalMs = isPro ? coachingInterval * 1000 : 600000; // 10 min for free
      coachTimerRef.current = setInterval(() => {
        setDistance((d) => {
          setElapsed((e) => {
            setWeather((w) => {
              setCurrentPace((p) => {
                if (isPro) {
                  fetchCoachMessage(d, p, e, w);
                } else {
                  // Text-only coaching for free users (no voice)
                  const prevQuiet = document.getElementById("quiet-mode-store")?.dataset?.quiet;
                  if (document.getElementById("quiet-mode-store")) {
                    document.getElementById("quiet-mode-store").dataset.quiet = "true";
                  }
                  fetchCoachMessage(d, p, e, w).then(() => {
                    if (document.getElementById("quiet-mode-store") && prevQuiet !== undefined) {
                      document.getElementById("quiet-mode-store").dataset.quiet = prevQuiet;
                    }
                  });
                }
                return p;
              });
              return w;
            });
            return e;
          });
          return d;
        });
      }, intervalMs);
    }
  }

  // ── PAUSE / RESUME ───────────────────────────────────────────────────────
  function pauseRun() {
    setPaused(true);
    clearInterval(timerRef.current);
    clearInterval(coachTimerRef.current);
    if (watchRef.current) {
      const isNative = window.Capacitor?.isNativePlatform?.();
      const CapGeo = window.Capacitor?.Plugins?.Geolocation;
      if (isNative && CapGeo) {
        CapGeo.clearWatch({ id: watchRef.current });
      } else {
        navigator.geolocation.clearWatch(watchRef.current);
      }
      watchRef.current = null;
    }
    speak("Run paused.");
  }

  function resumeRun() {
    setPaused(false);
    speak("Let's go!");
    const isNativeResume = window.Capacitor?.isNativePlatform?.();
    const CapGeoResume = window.Capacitor?.Plugins?.Geolocation;
    if (isNativeResume && CapGeoResume) {
      CapGeoResume.watchPosition({ enableHighAccuracy: true, timeout: 5000 }, (pos, err) => {
        if (err || !pos) return;
        const newPoint = { lat: pos.coords.latitude, lon: pos.coords.longitude, ts: Date.now() };
        setGpsAccuracy(Math.round(pos.coords.accuracy));
        if (pos.coords.accuracy > 50) return;
        setGpsPoints((prev) => {
          if (lastPointRef.current) {
            const added = haversineDistance(lastPointRef.current, newPoint);
            if (added < 0.1) setDistance((d) => d + added);
          }
          lastPointRef.current = newPoint;
          return [...prev, newPoint];
        });
      });
    } else {
      watchRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          const newPoint = { lat: pos.coords.latitude, lon: pos.coords.longitude, ts: Date.now() };
          setGpsAccuracy(Math.round(pos.coords.accuracy));
          if (pos.coords.accuracy > 50) return;
          setGpsPoints((prev) => {
            if (lastPointRef.current) {
              const added = haversineDistance(lastPointRef.current, newPoint);
              if (added < 0.1) setDistance((d) => d + added);
            }
            lastPointRef.current = newPoint;
            return [...prev, newPoint];
          });
        },
        (err) => console.error("GPS watch error:", err),
        { enableHighAccuracy: true, maximumAge: 2000, timeout: 5000 }
      );
    }
    timerRef.current = setInterval(() => {
      setElapsed((e) => {
        const newE = e + 1;
        setDistance((d) => {
          if (d > 0 && newE > 0) {
            const pace = newE / 60 / d;
            setCurrentPace(pace);
            setAvgPace(pace);
            setCalories(Math.round(d * 100));
          }
          return d;
        });
        return newE;
      });
    }, 1000);
    coachTimerRef.current = setInterval(() => {
      setDistance((d) => {
        setElapsed((e) => {
          setWeather((w) => {
            setCurrentPace((p) => {
              fetchCoachMessage(d, p, e, w);
              return p;
            });
            return w;
          });
          return e;
        });
        return d;
      });
    }, 90000);
  }

  // ── END RUN ─────────────────────────────────────────────────────────────
  async function endRun() {
    clearInterval(timerRef.current);
    clearInterval(coachTimerRef.current);
    if (watchRef.current) {
      const isNative = window.Capacitor?.isNativePlatform?.();
      const CapGeo = window.Capacitor?.Plugins?.Geolocation;
      if (isNative && CapGeo) {
        CapGeo.clearWatch({ id: watchRef.current });
      } else {
        navigator.geolocation.clearWatch(watchRef.current);
      }
      watchRef.current = null;
    }
    setRunning(false);

    setSaving(true);
    try {
      await supabase.from("runs").insert([{
        user_id: user?.id ?? "anonymous",
        duration_seconds: elapsed,
        distance_miles: parseFloat(distance.toFixed(3)),
        avg_pace: formatPace(avgPace),
        avg_hr: heartRate,
        calories: calories,
        gps_points: gpsPoints,
      }]);
      showToast("Run saved! 🎉");
    } catch (e) {
      console.error("Save error:", e);
      showToast("Save failed — check console.");
    } finally {
      setSaving(false);
    }

    // Snapshot values for share card before any state resets
    track("run_completed", { distance_miles: distance.toFixed(2), duration_seconds: elapsed, isPro });
    const snapDistance = distance;
    const snapElapsed = elapsed;
    const snapAvgPace = avgPace;
    const snapCalories = calories;
    const snapWeather = weather;

    setShareCardData({
      distance: snapDistance.toFixed(2),
      time: formatTime(snapElapsed),
      pace: formatPace(snapAvgPace),
      calories: snapCalories,
      weather: snapWeather,
      date: new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }),
    });

    // Generate debrief
    fetchDebrief(snapDistance, snapAvgPace, snapElapsed, snapWeather);
    setCoachMessage("Great run! Check your debrief below.");
    speak("Great run! Check your debrief below.");


  }

  // ── HISTORY ──────────────────────────────────────────────────────────────
  async function loadHistory() {
    const { data } = await supabase
      .from("runs")
      .select("*")
      .eq("user_id", user?.id ?? "anonymous")
      .order("created_at", { ascending: false })
      .limit(20);
    if (data) setRunHistory(data);
  }

  // Auto-initialize GPS when user logs in
  useEffect(() => {
    if (user && !gpsReady && !running) {
      enableGPS();
    }
  }, [user]);

  useEffect(() => {
    if (tab === "history") loadHistory();
    if (tab === "run" || tab === "routes") {
      // Give the DOM time to make the map visible, then resize + re-apply route
      setTimeout(() => {
        if (!sharedMapRef.current) return;
        const map = sharedMapRef.current;
        map.resize();
        if (suggestedRouteCoords) {
          const source = map.getSource("suggested");
          if (!source) return;
          source.setData({ type: "Feature", geometry: { type: "LineString", coordinates: suggestedRouteCoords } });
          const lons = suggestedRouteCoords.map(c => c[0]);
          const lats = suggestedRouteCoords.map(c => c[1]);
          map.fitBounds(
            [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]],
            { padding: 40, duration: 600 }
          );
        }
      }, 150);
    }
  }, [tab]);

  // ── TOAST ────────────────────────────────────────────────────────────────
  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  // ── STYLES ───────────────────────────────────────────────────────────────
  const s = {
    app: { fontFamily: "'Inter', sans-serif", background: "#0a0a0a", color: "#fff", minHeight: "100vh", maxWidth: 420, margin: "0 auto", padding: "0 0 80px" },
    header: { padding: "20px 20px 0", display: "flex", alignItems: "center", gap: 10 },
    logo: { fontSize: 22, fontWeight: 800, color: "#00ff88", letterSpacing: -0.5 },
    subtitle: { fontSize: 12, color: "#555", marginTop: 2 },
    tabs: { display: "flex", borderBottom: "1px solid #1a1a1a", margin: "16px 0 0" },
    tab: (active) => ({ flex: 1, padding: "12px", textAlign: "center", fontSize: 13, fontWeight: active ? 700 : 400, color: active ? "#00ff88" : "#555", borderBottom: active ? "2px solid #00ff88" : "2px solid transparent", cursor: "pointer", background: "none", border: "none", borderBottom: active ? "2px solid #00ff88" : "2px solid transparent" }),
    body: { padding: "16px 20px" },
    stat: { background: "#111", borderRadius: 12, padding: "14px 16px", flex: 1 },
    statVal: { fontSize: 28, fontWeight: 800, color: "#00ff88", lineHeight: 1 },
    statLabel: { fontSize: 11, color: "#555", marginTop: 4, textTransform: "uppercase", letterSpacing: 0.5 },
    statsRow: { display: "flex", gap: 10, marginBottom: 12 },
    btn: (color, disabled) => ({ width: "100%", padding: "16px", borderRadius: 14, fontSize: 16, fontWeight: 700, border: "none", cursor: disabled ? "not-allowed" : "pointer", background: disabled ? "#222" : color, color: disabled ? "#444" : "#000", transition: "all 0.2s", marginBottom: 10 }),
    coach: { background: "#0d1f0d", border: "1px solid #1a3a1a", borderRadius: 14, padding: 16, marginBottom: 12 },
    coachText: { fontSize: 15, color: "#cceecc", lineHeight: 1.6 },
    toast: { position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", background: "#00ff88", color: "#000", padding: "10px 20px", borderRadius: 20, fontWeight: 700, fontSize: 14, zIndex: 999, boxShadow: "0 4px 20px rgba(0,255,136,0.4)" },
    historyCard: { background: "#111", borderRadius: 12, padding: 16, marginBottom: 10 },
    debriefBox: { background: "#0d1a2a", border: "1px solid #1a3a5a", borderRadius: 14, padding: 16, marginTop: 12 },
    debriefText: { fontSize: 14, color: "#aaccee", lineHeight: 1.7 },
    gpsBar: { display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#111", borderRadius: 8, marginBottom: 10, fontSize: 12, flexWrap: "nowrap", overflow: "visible" },
    dot: (color) => ({ width: 8, height: 8, borderRadius: "50%", background: color }),
  };

  // ── ROUTE INTELLIGENCE ────────────────────────────────────────────────────
  async function generateRoutes() {
    if (!currentLocation) {
      showToast("Enable GPS first to get route suggestions.");
      return;
    }
    track("routes_generated", { goal_miles: routeGoal });
    setRouteLoading(true);
    setRouteOptions(null);
    setSuggestedRouteCoords(null);
    setSelectedRoute(null);

    try {
      const { lat, lon } = currentLocation;
      const goalMiles = parseFloat(routeGoal) || 3;
      const weatherContext = weather
        ? `Current weather: ${Math.round(weather.temp)}°F, ${weather.description}, wind ${Math.round(weather.windSpeed)} mph.`
        : "";

      // Ask AI to suggest 3 waypoint sets for different terrain types
      const prompt = `You are a local running route expert. A runner is at coordinates ${lat.toFixed(5)}, ${lon.toFixed(5)} and wants to run ${goalMiles} miles.

${weatherContext}

Suggest 3 different route options with different terrain profiles. For each route, provide 4-6 waypoints as lat/lon coordinates that form a loop back to the start. The waypoints should be on real roads or paths. Space them evenly to cover roughly ${goalMiles} miles total.

Respond ONLY with JSON, no other text:
{
  "routes": [
    {
      "name": "Flat & Fast",
      "terrain": "flat",
      "elevationGain": "~20 ft",
      "description": "Easy flat route along main roads, great for tempo runs",
      "weatherNote": "Good choice in current conditions",
      "waypoints": [[lon, lat], [lon, lat], [lon, lat], [lon, lat]]
    },
    {
      "name": "Rolling Hills",
      "terrain": "moderate",
      "elevationGain": "~80 ft",
      "description": "Mix of flat and gentle hills through residential streets",
      "weatherNote": "Moderate effort in this heat",
      "waypoints": [[lon, lat], [lon, lat], [lon, lat], [lon, lat]]
    },
    {
      "name": "Hill Challenge",
      "terrain": "hilly",
      "elevationGain": "~200 ft",
      "description": "Challenging hill route for strength building",
      "weatherNote": "Take it easy on the climbs in this weather",
      "waypoints": [[lon, lat], [lon, lat], [lon, lat], [lon, lat]]
    }
  ]
}`;

      const res = await fetch("/api/claude", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          messages: [{ role: "user", content: prompt }],
        }),
      });
      const data = await res.json();
      const text = data.content?.[0]?.text || "{}";
      const clean = text.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      setRouteOptions(parsed.routes);
    } catch (e) {
      console.error("Route generation error:", e);
      showToast("Could not generate routes. Try again.");
    } finally {
      setRouteLoading(false);
    }
  }

  async function selectRoute(route) {
    setSelectedRoute(route);
    if (!currentLocation) {
      showToast("No location — enable GPS first");
      return;
    }
    showToast("Loading route...");
    try {
      const { lat, lon } = currentLocation;
      const allPoints = [[lon, lat], ...route.waypoints, [lon, lat]];
      const coords = allPoints.map(p => p.join(",")).join(";");
      console.log("Fetching directions for", coords.substring(0, 80));
      const res = await fetch(
        `https://api.mapbox.com/directions/v5/mapbox/walking/${coords}?geometries=geojson&access_token=${MAPBOX_TOKEN}`
      );
      const data = await res.json();
      console.log("Directions response:", JSON.stringify(data).substring(0, 200));
      if (data.routes?.[0]?.geometry?.coordinates) {
        const routeCoords = data.routes[0].geometry.coordinates;
        GLOBAL_SUGGESTED_ROUTE = routeCoords;
        setSuggestedRouteCoords(routeCoords);
        showToast(`Route set — switch to Run tab`);
        // Directly update any live map instance — retry a few times to handle tab switch timing
        const applyToMap = (attempts) => {
          setTimeout(() => {
            if (sharedMapRef.current) {
              const src = sharedMapRef.current.getSource("suggested");
              if (src) {
                src.setData({ type: "Feature", geometry: { type: "LineString", coordinates: routeCoords } });
                const lons = routeCoords.map(c => c[0]);
                const lats = routeCoords.map(c => c[1]);
                sharedMapRef.current.fitBounds(
                  [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]],
                  { padding: 40, duration: 600 }
                );
              } else if (attempts > 0) {
                applyToMap(attempts - 1);
              }
            } else if (attempts > 0) {
              applyToMap(attempts - 1);
            }
          }, 300);
        };
        applyToMap(5);
      } else if (data.code) {
        console.error("Directions API error:", data.code, data.message);
        showToast("Directions API: " + (data.message || data.code));
        setSuggestedRouteCoords(allPoints);
      } else {
        GLOBAL_SUGGESTED_ROUTE = allPoints;
        setSuggestedRouteCoords(allPoints);
        showToast("Using straight-line route");
      }
    } catch (e) {
      console.error("Directions error:", e);
      showToast("Route error: " + e.message);
      GLOBAL_SUGGESTED_ROUTE = [[currentLocation.lon, currentLocation.lat], ...route.waypoints, [currentLocation.lon, currentLocation.lat]];
      setSuggestedRouteCoords(GLOBAL_SUGGESTED_ROUTE);
    }
  }

  // ── TRAINING PLAN ────────────────────────────────────────────────────────
  async function generateTrainingPlan() {
    track("training_plan_generated");
    setPlanLoading(true);
    try {
      const { data: runs } = await supabase
        .from("runs")
        .select("*")
        .eq("user_id", user?.id ?? "anonymous")
        .order("created_at", { ascending: false })
        .limit(10);

      if (!runs || runs.length < 1) {
        setTrainingPlan({ error: "Complete at least 1 run to generate a training plan." });
        setPlanLoading(false);
        return;
      }

      const runSummary = runs.map((r, i) =>
        `Run ${i + 1}: ${parseFloat(r.distance_miles).toFixed(2)} miles, pace ${r.avg_pace}/mi, duration ${formatTime(r.duration_seconds)}`
      ).join("\n");

      const totalMiles = runs.reduce((sum, r) => sum + parseFloat(r.distance_miles), 0);
      const avgMiles = (totalMiles / runs.length).toFixed(2);

      const prompt = `You are an expert running coach. Based on this runner's recent history, generate a personalized 7-day training plan.

Recent runs:
${runSummary}

Average run distance: ${avgMiles} miles
Number of runs on record: ${runs.length}

Generate a 7-day training plan. For each day provide:
- Day name (Monday through Sunday)
- Workout type (Easy Run, Tempo Run, Intervals, Long Run, Cross Training, Rest)
- Distance or duration
- One specific goal or focus for that workout

Format your response as JSON only, no other text, like this:
{
  "goal": "one sentence describing the runner's current fitness level and main goal",
  "days": [
    {"day": "Monday", "type": "Rest", "workout": "Full rest or light stretching", "goal": "Recovery"},
    {"day": "Tuesday", "type": "Easy Run", "workout": "3 miles", "goal": "Build aerobic base at conversational pace"}
  ]
}`;

      const res = await fetch("/api/claude", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          messages: [{ role: "user", content: prompt }],
        }),
      });
      const data = await res.json();
      const text = data.content?.[0]?.text || "{}";
      const clean = text.replace(/```json|```/g, "").trim();
      const plan = JSON.parse(clean);
      setTrainingPlan(plan);
    } catch (e) {
      console.error("Training plan error:", e);
      setTrainingPlan({ error: "Could not generate plan. Try again." });
    } finally {
      setPlanLoading(false);
    }
  }

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div style={s.app}>
      {toast && <div style={s.toast}>{toast}</div>}
      <div id="hr-data-store" data-hr={heartRate || ""} style={{ display: "none" }} />
      <div id="quiet-mode-store" data-quiet={quietMode ? "true" : "false"} style={{ display: "none" }} />
      <div id="voice-count-store" data-count={voiceMessagesUsed} data-limit={VOICE_MESSAGE_LIMIT} data-running={running ? "true" : "false"} style={{ display: "none" }} />

      <div style={s.header}>
        <div style={{ flex: 1 }}>
          <div style={s.logo}>⚡ VELOCITY AI</div>
          <div style={s.subtitle}>AI-Powered Running Coach</div>
        </div>
        {user && (
          <button onClick={() => setShowSettings(!showSettings)} style={{ background: "none", border: "none", color: "#555", fontSize: 20, cursor: "pointer", padding: "4px 8px" }}>
            ⚙️
          </button>
        )}
        {user && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <img src={user.user_metadata?.avatar_url} alt="" style={{ width: 30, height: 30, borderRadius: "50%", border: "2px solid #00ff88" }} />
            {isPro && (
              <span style={{ background: "#00ff88", color: "#000", borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>PRO</span>
            )}
            {isPro && (
              <button onClick={cancelSubscription} style={{ background: "none", border: "1px solid #333", color: "#555", borderRadius: 8, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>
                Cancel Pro
              </button>
            )}
            <button onClick={signOut} style={{ background: "none", border: "1px solid #333", color: "#555", borderRadius: 8, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>
              Sign Out
            </button>
          </div>
        )}
      </div>

      {/* Settings panel */}
      {showSettings && user && (
        <div style={{ background: "#111", border: "1px solid #1a1a1a", margin: "0 20px 12px", borderRadius: 14, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#ccc", marginBottom: 14 }}>Settings</div>

          {/* Coaching frequency */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: "#555", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Coaching Frequency
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {[
                { label: "3 min", val: 180 },
                { label: "5 min", val: 300 },
                { label: "10 min", val: 600 },
              ].map((opt) => (
                <button
                  key={opt.val}
                  onClick={() => { setCoachingInterval(opt.val); setManualMode(false); }}
                  style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "none", background: !manualMode && coachingInterval === opt.val ? "#00ff88" : "#1a1a1a", color: !manualMode && coachingInterval === opt.val ? "#000" : "#555", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
                >
                  {opt.label}
                </button>
              ))}
              <button
                onClick={() => setManualMode(true)}
                style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "none", background: manualMode ? "#00ff88" : "#1a1a1a", color: manualMode ? "#000" : "#555", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
              >
                Manual
              </button>
            </div>
            {manualMode && (
              <div style={{ fontSize: 11, color: "#555", marginTop: 6 }}>Tap the coach button during your run to get coaching on demand.</div>
            )}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 12, color: "#ccc", fontWeight: 600 }}>Quiet Mode</div>
              <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>Show coaching on screen, no voice</div>
            </div>
            <button
              onClick={() => setQuietMode(!quietMode)}
              style={{ width: 44, height: 24, borderRadius: 12, border: "none", background: quietMode ? "#00ff88" : "#333", cursor: "pointer", position: "relative", transition: "background 0.2s" }}
            >
              <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: quietMode ? 23 : 3, transition: "left 0.2s" }} />
            </button>
          </div>

          {/* Feedback link */}
          <div style={{ borderTop: "1px solid #1a1a1a", paddingTop: 14 }}>
            <a
              href="https://tally.so/r/0QLbkB"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", textDecoration: "none" }}
            >
              <div>
                <div style={{ fontSize: 12, color: "#ccc", fontWeight: 600 }}>Send Feedback</div>
                <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>Help us improve Velocity AI</div>
              </div>
              <span style={{ color: "#555", fontSize: 16 }}>→</span>
            </a>
          </div>
        </div>
      )}

      {authLoading && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh", color: "#555" }}>
          Loading...
        </div>
      )}

      {!authLoading && !user && (
        <div style={{ minHeight: "100vh", background: "#0a0a0a", color: "#fff", fontFamily: "'Inter', sans-serif", overflowX: "hidden" }}>

          {/* Hero */}
          <div style={{ position: "relative", padding: "60px 24px 48px", textAlign: "center", overflow: "hidden" }}>
            {/* Background glow */}
            <div style={{ position: "absolute", top: -100, left: "50%", transform: "translateX(-50%)", width: 600, height: 600, background: "radial-gradient(circle, rgba(0,255,136,0.08) 0%, transparent 65%)", pointerEvents: "none" }} />

            <div style={{ display: "inline-block", background: "rgba(0,255,136,0.1)", border: "1px solid rgba(0,255,136,0.3)", borderRadius: 20, padding: "4px 14px", fontSize: 12, color: "#00ff88", fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", marginBottom: 24 }}>
              AI-Powered Running Coach
            </div>

            <h1 style={{ fontSize: "clamp(36px, 8vw, 56px)", fontWeight: 900, lineHeight: 1.1, marginBottom: 20, letterSpacing: -1 }}>
              Your Coach.<br />
              <span style={{ color: "#00ff88" }}>In Your Ear.</span><br />
              Every Run.
            </h1>

            <p style={{ fontSize: 17, color: "#888", lineHeight: 1.7, maxWidth: 380, margin: "0 auto 40px", fontWeight: 400 }}>
              Velocity AI gives you a real AI coach that speaks to you mid-run — analyzing your pace, fatigue, and live weather every 5 minutes.
            </p>

            <button
              onClick={signInWithGoogle}
              style={{ background: "#00ff88", color: "#000", border: "none", borderRadius: 14, padding: "16px 32px", fontSize: 17, fontWeight: 800, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 12, boxShadow: "0 0 40px rgba(0,255,136,0.3)" }}
            >
              <img src="https://www.google.com/favicon.ico" alt="" style={{ width: 20, height: 20 }} />
              Start Running Free
            </button>

            <div style={{ marginBottom: 14 }}>
              <button
                onClick={signInWithApple}
                style={{ background: "#fff", color: "#000", border: "none", borderRadius: 14, padding: "16px 32px", fontSize: 17, fontWeight: 800, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 10 }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="black"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                Sign in with Apple
              </button>
            </div>

            <div style={{ fontSize: 13, color: "#444" }}>No app store. Works on any phone. Free to start.</div>
          </div>

          {/* Features */}
          <div style={{ padding: "0 24px 48px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, maxWidth: 500, margin: "0 auto" }}>
              {[
                { icon: "🤖", title: "AI Mid-Run Coach", desc: "Real coaching every 5 minutes based on your actual data" },
                { icon: "🗺", title: "Satellite GPS Map", desc: "Live route drawing on real satellite imagery" },
                { icon: "🌡", title: "Live Weather", desc: "Coach adapts advice to your exact conditions" },
                { icon: "📋", title: "Training Plans", desc: "Personalized weekly plans from your run history" },
                { icon: "🎯", title: "Route Intelligence", desc: "AI suggests routes based on terrain and distance goal" },
                { icon: "🔊", title: "Real Coach Voice", desc: "ElevenLabs voice that sounds like an actual coach" },
              ].map((f) => (
                <div key={f.title} style={{ background: "#111", border: "1px solid #1a1a1a", borderRadius: 14, padding: 16 }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>{f.icon}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", marginBottom: 4 }}>{f.title}</div>
                  <div style={{ fontSize: 11, color: "#555", lineHeight: 1.5 }}>{f.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Comparison */}
          <div style={{ padding: "0 24px 48px", maxWidth: 500, margin: "0 auto" }}>
            <div style={{ background: "#111", border: "1px solid #1a1a1a", borderRadius: 16, padding: 20 }}>
              <div style={{ fontSize: 13, color: "#555", marginBottom: 16, textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>How it compares</div>
              {[
                { label: "Garmin", val: "Shows your pace. $300+ hardware.", good: false },
                { label: "Velocity AI", val: "Coaches you in real time. $9.99/mo.", good: true },
              ].map((r) => (
                <div key={r.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #1a1a1a" }}>
                  <span style={{ fontWeight: 700, color: r.good ? "#00ff88" : "#555", fontSize: 14 }}>{r.label}</span>
                  <span style={{ fontSize: 12, color: r.good ? "#aaa" : "#444", maxWidth: 200, textAlign: "right" }}>{r.val}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing */}
          <div style={{ padding: "0 24px 48px", maxWidth: 500, margin: "0 auto" }}>
            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1, background: "#111", border: "1px solid #1a1a1a", borderRadius: 16, padding: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#555", marginBottom: 8 }}>FREE</div>
                <div style={{ fontSize: 28, fontWeight: 900, color: "#fff", marginBottom: 12 }}>$0</div>
                {["GPS tracking", "Satellite map", "Run history", "Basic stats"].map(f => (
                  <div key={f} style={{ fontSize: 12, color: "#555", marginBottom: 6 }}>✓ {f}</div>
                ))}
              </div>
              <div style={{ flex: 1, background: "#0d200d", border: "1px solid #00ff88", borderRadius: 16, padding: 20, position: "relative" }}>
                <div style={{ position: "absolute", top: -10, right: 12, background: "#00ff88", color: "#000", fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 10 }}>POPULAR</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#00ff88", marginBottom: 8 }}>PRO</div>
                <div style={{ fontSize: 28, fontWeight: 900, color: "#fff", marginBottom: 12 }}>$9.99<span style={{ fontSize: 13, fontWeight: 400, color: "#555" }}>/mo</span></div>
                {["AI coaching every 5 min", "Real coach voice", "Live weather coaching", "Training plans", "Route intelligence", "Post-run debrief"].map(f => (
                  <div key={f} style={{ fontSize: 12, color: "#aaa", marginBottom: 6 }}>✓ {f}</div>
                ))}
                {window.Capacitor?.isNativePlatform?.() && (
                  <button
                    onClick={signInAndPurchase}
                    style={{ width: "100%", marginTop: 12, padding: "10px", background: "#00ff88", color: "#000", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                  >
                    Subscribe $9.99/mo
                  </button>
                )}
                <div style={{ marginTop: 8, fontSize: 10, color: "#555", textAlign: "center" }}>
                  Auto-renews monthly. Cancel anytime.<br/>
                  <span onClick={() => { window.open("https://velocityai.run/terms.html","_blank"); }} style={{ color: "#00ff88", textDecoration: "underline", cursor: "pointer" }}>Terms of Use</span> · <span onClick={() => { window.open("https://velocityai.run/privacy.html","_blank"); }} style={{ color: "#00ff88", textDecoration: "underline", cursor: "pointer" }}>Privacy Policy</span>
                </div>
              </div>
            </div>
          </div>

          {/* Email capture */}
          <div style={{ padding: "0 24px 48px", maxWidth: 500, margin: "0 auto", textAlign: "center" }}>
            <div style={{ background: "#111", border: "1px solid #1a1a1a", borderRadius: 16, padding: 24 }}>
              {!emailSubmitted ? (
                <>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#fff", marginBottom: 6 }}>Not ready to sign up yet?</div>
                  <div style={{ fontSize: 13, color: "#555", marginBottom: 16 }}>Get notified when we add Spotify integration and Apple Watch support.</div>
                  <form onSubmit={captureEmail} style={{ display: "flex", gap: 8 }}>
                    <input
                      type="email"
                      placeholder="your@email.com"
                      value={emailInput}
                      onChange={e => setEmailInput(e.target.value)}
                      style={{ flex: 1, background: "#1a1a1a", border: "1px solid #333", borderRadius: 10, padding: "10px 14px", color: "#fff", fontSize: 14, outline: "none" }}
                    />
                    <button
                      type="submit"
                      disabled={emailLoading}
                      style={{ background: emailLoading ? "#222" : "#00ff88", color: "#000", border: "none", borderRadius: 10, padding: "10px 18px", fontSize: 13, fontWeight: 700, cursor: emailLoading ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}
                    >
                      {emailLoading ? "..." : "Notify Me"}
                    </button>
                  </form>
                </>
              ) : (
                <div style={{ color: "#00ff88", fontSize: 15, fontWeight: 700 }}>
                  ✓ You're on the list! We'll let you know when new features drop.
                </div>
              )}
            </div>
          </div>

          {/* CTA */}
          <div style={{ padding: "0 24px 80px", textAlign: "center" }}>
            <button
              onClick={signInWithGoogle}
              style={{ background: "#00ff88", color: "#000", border: "none", borderRadius: 14, padding: "16px 32px", fontSize: 17, fontWeight: 800, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 12, boxShadow: "0 0 40px rgba(0,255,136,0.3)" }}
            >
              <img src="https://www.google.com/favicon.ico" alt="" style={{ width: 20, height: 20 }} />
              Try Velocity AI Free
            </button>
            <div style={{ marginBottom: 12 }}>
              <button
                onClick={signInWithApple}
                style={{ background: "#fff", color: "#000", border: "none", borderRadius: 14, padding: "16px 32px", fontSize: 17, fontWeight: 800, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 10 }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="black"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                Sign in with Apple
              </button>
            </div>
            <div style={{ fontSize: 12, color: "#333", marginTop: 12 }}>No credit card required · Cancel anytime</div>
          </div>
        </div>
      )}

      {!authLoading && user && showOnboarding && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ background: "#0d0d0d", border: "1px solid #1a1a1a", borderRadius: 24, padding: 32, maxWidth: 360, width: "100%", textAlign: "center" }}>
            {/* Progress dots */}
            <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 32 }}>
              {onboardingSteps.map((_, i) => (
                <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: i === onboardingStep ? "#00ff88" : "#222", transition: "background 0.3s" }} />
              ))}
            </div>

            <div style={{ fontSize: 56, marginBottom: 16 }}>{onboardingSteps[onboardingStep].emoji}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", marginBottom: 12 }}>{onboardingSteps[onboardingStep].title}</div>
            <div style={{ fontSize: 15, color: "#666", lineHeight: 1.7, marginBottom: 32 }}>{onboardingSteps[onboardingStep].desc}</div>

            <button
              onClick={() => {
                if (onboardingStep < onboardingSteps.length - 1) {
                  setOnboardingStep(onboardingStep + 1);
                } else {
                  completeOnboarding();
                }
              }}
              style={{ width: "100%", padding: "14px", background: "#00ff88", color: "#000", border: "none", borderRadius: 14, fontSize: 16, fontWeight: 800, cursor: "pointer" }}
            >
              {onboardingSteps[onboardingStep].btn}
            </button>

            {onboardingStep < onboardingSteps.length - 1 && (
              <button onClick={completeOnboarding} style={{ background: "none", border: "none", color: "#333", fontSize: 13, cursor: "pointer", marginTop: 12 }}>
                Skip
              </button>
            )}
          </div>
        </div>
      )}

      {!authLoading && user && (
      <>
      <div style={s.tabs}>
        <button style={s.tab(tab === "run")} onClick={() => setTab("run")}>🏃 Run</button>
        <button style={s.tab(tab === "routes")} onClick={() => setTab("routes")}>🗺 Routes</button>
        <button style={s.tab(tab === "training")} onClick={() => { setTab("training"); if (!trainingPlan) generateTrainingPlan(); }}>📋 Plan</button>
        <button style={s.tab(tab === "history")} onClick={() => setTab("history")}>📊 History</button>
      </div>

      {/* Map rendered per-tab — reads from GLOBAL_SUGGESTED_ROUTE on init */}
      {(tab === "run" || tab === "routes") && (
        <div style={{ padding: "0 20px", marginBottom: 0 }}>
          <RouteMap points={tab === "run" ? gpsPoints : []} suggestedRoute={suggestedRouteCoords} onMapReady={(map) => {
          sharedMapRef.current = map;
          // If a route is already selected, draw it immediately on this new map instance
          if (GLOBAL_SUGGESTED_ROUTE && GLOBAL_SUGGESTED_ROUTE.length > 0) {
            const src = map.getSource("suggested");
            if (src) {
              src.setData({ type: "Feature", geometry: { type: "LineString", coordinates: GLOBAL_SUGGESTED_ROUTE } });
              const lons = GLOBAL_SUGGESTED_ROUTE.map(c => c[0]);
              const lats = GLOBAL_SUGGESTED_ROUTE.map(c => c[1]);
              map.fitBounds([[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]], { padding: 40, duration: 600 });
            }
          }
        }} />
        </div>
      )}

      <div style={s.body}>
        {tab === "run" && (
          <>
            {/* Upgrade banner for free users */}
            {!isPro && (
              <div style={{ background: "#1a1500", border: "1px solid #3a3000", borderRadius: 10, padding: "10px 16px", marginBottom: 10 }}>
                {window.Capacitor?.isNativePlatform?.() ? (
                  <div>
                    <div style={{ fontSize: 13, color: "#aa8800", marginBottom: 8 }}>⚡ Upgrade to Pro — $9.99/month</div>
                    <button onClick={startCheckout} style={{ background: "#ffcc00", color: "#000", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", marginRight: 8 }}>
                      Upgrade Now
                    </button>
                    <button onClick={restorePurchases} style={{ background: "transparent", color: "#555", border: "1px solid #333", borderRadius: 8, padding: "8px 12px", fontSize: 12, cursor: "pointer" }}>
                      Restore
                    </button>
                    <div style={{ marginTop: 8, fontSize: 10, color: "#555" }}>
                      $9.99/month, auto-renews. <span onClick={() => { window.open("https://velocityai.run/terms.html","_blank"); }} style={{ color: "#00ff88", textDecoration: "underline", cursor: "pointer" }}>Terms</span> · <span onClick={() => { window.open("https://velocityai.run/privacy.html","_blank"); }} style={{ color: "#00ff88", textDecoration: "underline", cursor: "pointer" }}>Privacy</span>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, color: "#aa8800" }}>⚡ Free plan — 1 AI message/run</span>
                    <button onClick={startCheckout} style={{ background: "#ffcc00", color: "#000", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                      Upgrade $9.99/mo
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* GPS status */}
            <div style={s.gpsBar}>
              <div style={s.dot(gpsReady ? "#00ff88" : "#ff4444")} />
              <span style={{ color: gpsReady ? "#00ff88" : "#ff6666" }}>
                {gpsReady ? `GPS Ready (±${gpsAccuracy}m)` : "GPS not enabled"}
              </span>
              <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
                {weather && <span style={{ color: "#888" }}>🌡 {Math.round(weather.temp)}°F</span>}
                {!hrDevice ? (
                  <button onClick={connectHRMonitor} disabled={hrConnecting} style={{ background: "none", border: "1px solid #333", color: hrConnecting ? "#444" : "#888", borderRadius: 6, padding: "2px 8px", fontSize: 11, cursor: "pointer" }}>
                    {hrConnecting ? "Connecting..." : "❤️ HR"}
                  </button>
                ) : (
                  <button onClick={disconnectHRMonitor} style={{ background: "none", border: "1px solid #ff4444", color: "#ff6666", borderRadius: 6, padding: "2px 8px", fontSize: 11, cursor: "pointer" }}>
                    ❤️ {heartRate} bpm
                  </button>
                )}
              </span>
            </div>

            {/* Weather bar */}
            <WeatherBar weather={weather} />
            {weather && <div style={{ marginBottom: 10 }} />}

            {/* Stats */}
            <div style={s.statsRow}>
              <div style={s.stat}>
                <div style={s.statVal}>{distance.toFixed(2)}</div>
                <div style={s.statLabel}>Miles</div>
              </div>
              <div style={s.stat}>
                <div style={s.statVal}>{formatTime(elapsed)}</div>
                <div style={s.statLabel}>Time</div>
              </div>
              <div style={s.stat}>
                <div style={s.statVal}>{formatPace(currentPace)}</div>
                <div style={s.statLabel}>Pace/mi</div>
              </div>
            </div>

            <div style={s.statsRow}>
              <div style={s.stat}>
                <div style={s.statVal}>{calories}</div>
                <div style={s.statLabel}>Calories</div>
              </div>
              <div style={s.stat}>
                <div style={s.statVal}>{heartRate || "--"}</div>
                <div style={s.statLabel}>BPM</div>
              </div>
              <div style={s.stat}>
                <div style={s.statVal}>{cadence || "--"}</div>
                <div style={s.statLabel}>Cadence</div>
              </div>
            </div>

            {/* Route map is rendered above tabs for persistence */}

            {/* AI Coach box */}
            <div style={s.coach}>
              <div style={{ fontSize: 11, color: "#00ff88", fontWeight: 700, marginBottom: 8, textTransform: "uppercase", letterSpacing: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>{coachLoading ? "⏳ Coach thinking..." : "🤖 AI Coach"}</span>
                {running && (
                  <button
                    onClick={() => setQuietMode(!quietMode)}
                    style={{ background: quietMode ? "#1a3a1a" : "none", border: "1px solid #1a3a1a", color: quietMode ? "#00ff88" : "#333", borderRadius: 6, padding: "2px 8px", fontSize: 10, cursor: "pointer", fontWeight: 600 }}
                  >
                    {quietMode ? "🔇 Quiet" : "🔊 Voice"}
                  </button>
                )}
              </div>
              <div style={s.coachText}>{coachMessage}</div>
              {running && !coachLoading && (
                <div style={{ marginTop: 10, fontSize: 11, color: "#555", display: "flex", alignItems: "center", gap: 6 }}>
                  🎵 <span>If your music paused, tap play in your music app to resume</span>
                </div>
              )}
              {running && voiceMessagesUsed >= VOICE_MESSAGE_LIMIT && (
                <div style={{ marginTop: 8, fontSize: 11, color: "#555", display: "flex", alignItems: "center", gap: 6 }}>
                  🔇 <span>Voice coaching complete — coaching continues on screen</span>
                </div>
              )}
              {running && manualMode && isPro && (
                <button
                  onClick={() => {
                    setDistance((d) => { setElapsed((e) => { setWeather((w) => { setCurrentPace((p) => { fetchCoachMessage(d, p, e, w); return p; }); return w; }); return e; }); return d; });
                  }}
                  disabled={coachLoading || voiceMessagesUsed >= VOICE_MESSAGE_LIMIT}
                  style={{ marginTop: 10, width: "100%", padding: "10px", borderRadius: 10, border: "1px solid #00ff88", background: "#0d200d", color: "#00ff88", fontSize: 13, fontWeight: 700, cursor: coachLoading ? "not-allowed" : "pointer" }}
                >
                  {coachLoading ? "Coaching..." : "⚡ Get Coaching Now"}
                </button>
              )}
            </div>

            {/* Post-run debrief */}
            {/* Post-run star rating */}
            {!running && debrief && !ratingSubmitted && (
              <div style={{ background: "#111", border: "1px solid #1a1a1a", borderRadius: 14, padding: 16, marginBottom: 12, textAlign: "center" }}>
                <div style={{ fontSize: 13, color: "#ccc", fontWeight: 600, marginBottom: 12 }}>How was your coaching experience?</div>
                <div style={{ display: "flex", justifyContent: "center", gap: 12, marginBottom: 12 }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={async () => {
                        setRunRating(star);
                        setRatingSubmitted(true);
                        track("run_rated", { rating: star });
                        await supabase.from("run_ratings").insert([{ user_id: user?.id, rating: star, created_at: new Date().toISOString() }]);
                      }}
                      style={{ background: "none", border: "none", fontSize: 28, cursor: "pointer", opacity: runRating >= star ? 1 : 0.3, transition: "opacity 0.1s" }}
                    >
                      ⭐
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: "#555" }}>Tap a star to rate</div>
              </div>
            )}

            {!running && ratingSubmitted && (
              <div style={{ background: "#0d200d", border: "1px solid #1a3a1a", borderRadius: 14, padding: 14, marginBottom: 12, textAlign: "center" }}>
                <div style={{ fontSize: 13, color: "#00ff88", fontWeight: 600 }}>
                  {runRating >= 4 ? "Love the feedback! Keep running 🏃" : "Thanks — we'll use this to improve 🙏"}
                </div>
              </div>
            )}

            {/* Ask Coach - only during run */}
            {running && (
              <div style={{ marginBottom: 12 }}>
                <button
                  onClick={askCoach}
                  disabled={isListening || replyLoading}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: 12,
                    border: `1px solid ${isListening ? "#00ff88" : "#1a1a1a"}`,
                    background: isListening ? "#0d200d" : "#111",
                    color: isListening ? "#00ff88" : "#555",
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: isListening || replyLoading ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    transition: "all 0.2s",
                  }}
                >
                  {isListening ? "🎤 Listening..." : replyLoading ? "⏳ Coach is thinking..." : "🎤 Ask Your Coach"}
                </button>

                {runnerQuestion && (
                  <div style={{ marginTop: 8, background: "#1a1a1a", borderRadius: 10, padding: "10px 14px" }}>
                    <div style={{ fontSize: 11, color: "#555", marginBottom: 4 }}>You asked:</div>
                    <div style={{ fontSize: 13, color: "#aaa", fontStyle: "italic" }}>"{runnerQuestion}"</div>
                  </div>
                )}

                {coachReply && (
                  <div style={{ marginTop: 6, background: "#0d200d", border: "1px solid #1a3a1a", borderRadius: 10, padding: "10px 14px" }}>
                    <div style={{ fontSize: 11, color: "#00ff88", fontWeight: 700, marginBottom: 4 }}>Coach:</div>
                    <div style={{ fontSize: 13, color: "#cceecc", lineHeight: 1.6 }}>{coachReply}</div>
                  </div>
                )}
              </div>
            )}

            {(debrief || debriefLoading) && (
              <div style={s.debriefBox}>
                <div style={{ fontSize: 11, color: "#4488cc", fontWeight: 700, marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>
                  📋 Post-Run Debrief
                </div>
                <div style={s.debriefText}>
                  {debriefLoading ? "Analyzing your run..." : debrief}
                </div>
                {debrief && shareCardData && (
                  <button
                    onClick={() => { setShowShareCard(true); track("share_card_viewed", { distance: shareCardData?.distance }); }}
                    style={{ marginTop: 12, width: "100%", padding: "10px", background: "#1a2a3a", border: "1px solid #2a4a6a", borderRadius: 10, color: "#4488cc", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                  >
                    📸 View Share Card
                  </button>
                )}
              </div>
            )}

            {/* Share Card Modal */}
            {showShareCard && shareCardData && (
              <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
                <div style={{ width: "100%", maxWidth: 380 }}>
                  {/* The card itself */}
                  <div id="share-card" style={{ background: "linear-gradient(135deg, #0a1a0a 0%, #0a0a1a 100%)", border: "1px solid #00ff88", borderRadius: 20, padding: 24, position: "relative", overflow: "hidden" }}>
                    {/* Background accent */}
                    <div style={{ position: "absolute", top: -40, right: -40, width: 150, height: 150, background: "radial-gradient(circle, rgba(0,255,136,0.08) 0%, transparent 70%)", borderRadius: "50%" }} />

                    {/* Header */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                      <div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: "#00ff88", letterSpacing: -0.5 }}>⚡ VELOCITY AI</div>
                        <div style={{ fontSize: 11, color: "#444", marginTop: 2 }}>{shareCardData.date}</div>
                      </div>
                      <div style={{ fontSize: 28 }}>🏃</div>
                    </div>

                    {/* Main stat */}
                    <div style={{ textAlign: "center", marginBottom: 20 }}>
                      <div style={{ fontSize: 64, fontWeight: 900, color: "#fff", lineHeight: 1, letterSpacing: -2 }}>{shareCardData.distance}</div>
                      <div style={{ fontSize: 14, color: "#555", textTransform: "uppercase", letterSpacing: 2 }}>miles</div>
                    </div>

                    {/* Stats row */}
                    <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
                      {[
                        { val: shareCardData.time, label: "Time" },
                        { val: shareCardData.pace, label: "Avg Pace" },
                        { val: shareCardData.calories, label: "Calories" },
                      ].map((s) => (
                        <div key={s.label} style={{ flex: 1, background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: "10px 6px", textAlign: "center" }}>
                          <div style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>{s.val}</div>
                          <div style={{ fontSize: 10, color: "#444", marginTop: 2 }}>{s.label}</div>
                        </div>
                      ))}
                    </div>

                    {/* Weather */}
                    {shareCardData.weather && (
                      <div style={{ fontSize: 12, color: "#444", marginBottom: 16, textAlign: "center" }}>
                        🌡 {Math.round(shareCardData.weather.temp)}°F · {shareCardData.weather.description} · 💨 {Math.round(shareCardData.weather.windSpeed)} mph
                      </div>
                    )}

                    {/* AI debrief quote */}
                    {debrief && (
                      <div style={{ background: "rgba(0,255,136,0.05)", border: "1px solid rgba(0,255,136,0.15)", borderRadius: 12, padding: 14, marginBottom: 16 }}>
                        <div style={{ fontSize: 11, color: "#00ff88", fontWeight: 700, marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>AI Coach</div>
                        <div style={{ fontSize: 12, color: "#aaa", lineHeight: 1.6, fontStyle: "italic" }}>
                          "{debrief.length > 180 ? debrief.slice(0, 180) + "..." : debrief}"
                        </div>
                      </div>
                    )}

                    {/* Footer */}
                    <div style={{ textAlign: "center", fontSize: 11, color: "#333" }}>paceai.app · AI-Powered Running Coach</div>
                  </div>

                  {/* Buttons */}
                  <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                    <button
                      onClick={() => {
                        if (navigator.share) {
                          navigator.share({ title: "My Run — Velocity AI", text: `Just ran ${shareCardData.distance} miles in ${shareCardData.time} at ${shareCardData.pace}/mi with my AI coach! 🏃⚡`, url: "https://velocityai.run" });
                        } else {
                          navigator.clipboard.writeText(`Just ran ${shareCardData.distance} miles in ${shareCardData.time} at ${shareCardData.pace}/mi with my AI coach! 🏃⚡ velocityai.run`);
                          showToast("Copied to clipboard!");
                        }
                      }}
                      style={{ flex: 1, padding: 12, background: "#00ff88", color: "#000", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
                    >
                      📤 Share
                    </button>
                    <button
                      onClick={() => setShowShareCard(false)}
                      style={{ padding: "12px 20px", background: "none", border: "1px solid #333", color: "#555", borderRadius: 12, fontSize: 14, cursor: "pointer" }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Buttons */}
            {!gpsReady && !running && (
              <div style={{ textAlign: "center", padding: "12px 0", fontSize: 13, color: "#555" }}>
                📍 Getting GPS ready...
              </div>
            )}

            {gpsReady && !running && (
              <button style={s.btn("#00ff88", false)} onClick={startRun}>
                ▶ START RUN
              </button>
            )}

            {running && !paused && (
              <div style={{ display: "flex", gap: 10 }}>
                <button style={{ ...s.btn("#ffaa00", false), flex: 1 }} onClick={pauseRun}>
                  ⏸ PAUSE
                </button>
                <button style={{ ...s.btn("#ff4444", saving), flex: 1 }} onClick={endRun} disabled={saving}>
                  {saving ? "Saving..." : "⏹ END + SAVE"}
                </button>
              </div>
            )}

            {running && paused && (
              <div style={{ display: "flex", gap: 10 }}>
                <button style={{ ...s.btn("#00ff88", false), flex: 1 }} onClick={resumeRun}>
                  ▶ RESUME
                </button>
                <button style={{ ...s.btn("#ff4444", saving), flex: 1 }} onClick={endRun} disabled={saving}>
                  {saving ? "Saving..." : "⏹ END + SAVE"}
                </button>
              </div>
            )}
          </>
        )}

        {tab === "routes" && (
          <>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4, color: "#ccc" }}>Route Intelligence</div>
            <div style={{ fontSize: 13, color: "#555", marginBottom: 14 }}>AI-suggested routes based on your distance goal, terrain, and current weather.</div>

            {!isPro && (
              <div style={{ background: "#1a1500", border: "1px solid #3a3000", borderRadius: 10, padding: "12px 16px", marginBottom: 14 }}>
                {window.Capacitor?.isNativePlatform?.() ? (
                  <div>
                    <div style={{ fontSize: 13, color: "#aa8800", marginBottom: 8 }}>🔒 Pro feature — $9.99/month</div>
                    <button onClick={startCheckout} style={{ background: "#ffcc00", color: "#000", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", marginRight: 8 }}>
                      Upgrade Now
                    </button>
                    <button onClick={restorePurchases} style={{ background: "transparent", color: "#555", border: "1px solid #333", borderRadius: 8, padding: "8px 12px", fontSize: 12, cursor: "pointer" }}>
                      Restore
                    </button>
                    <div style={{ marginTop: 8, fontSize: 10, color: "#555" }}>
                      $9.99/month, auto-renews. <span onClick={() => { window.open("https://velocityai.run/terms.html","_blank"); }} style={{ color: "#00ff88", textDecoration: "underline", cursor: "pointer" }}>Terms</span> · <span onClick={() => { window.open("https://velocityai.run/privacy.html","_blank"); }} style={{ color: "#00ff88", textDecoration: "underline", cursor: "pointer" }}>Privacy</span>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, color: "#aa8800" }}>🔒 Pro feature</span>
                    <button onClick={startCheckout} style={{ background: "#ffcc00", color: "#000", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                      Upgrade $9.99/mo
                    </button>
                  </div>
                )}
              </div>
            )}

            {isPro && (
              <>
                {/* Distance goal input */}
                <div style={{ background: "#111", borderRadius: 12, padding: 16, marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: "#555", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Distance Goal (miles)</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {["2", "3", "5", "6", "10"].map((d) => (
                      <button
                        key={d}
                        onClick={() => setRouteGoal(d)}
                        style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "none", background: routeGoal === d ? "#00ff88" : "#1a1a1a", color: routeGoal === d ? "#000" : "#555", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Map preview shown above */}
                {suggestedRouteCoords && (
                  <div style={{ fontSize: 11, color: "#4488ff", textAlign: "center", marginBottom: 8, marginTop: -8 }}>
                    🔵 Blue dashed line = suggested route
                  </div>
                )}

                <button
                  onClick={generateRoutes}
                  disabled={routeLoading || !gpsReady}
                  style={{ width: "100%", padding: 14, borderRadius: 12, border: "none", background: routeLoading || !gpsReady ? "#222" : "#00ff88", color: routeLoading || !gpsReady ? "#444" : "#000", fontWeight: 700, fontSize: 15, cursor: routeLoading || !gpsReady ? "not-allowed" : "pointer", marginBottom: 14 }}
                >
                  {routeLoading ? "Generating routes..." : !gpsReady ? "Enable GPS first" : "🗺 Generate Routes"}
                </button>

                {/* Route options */}
                {routeOptions && routeOptions.map((route, i) => (
                  <div
                    key={i}
                    onClick={() => selectRoute(route)}
                    style={{ background: selectedRoute?.name === route.name ? "#0d2010" : "#111", border: `1px solid ${selectedRoute?.name === route.name ? "#00ff88" : "#222"}`, borderRadius: 12, padding: 14, marginBottom: 10, cursor: "pointer" }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <span style={{ fontWeight: 700, color: selectedRoute?.name === route.name ? "#00ff88" : "#fff", fontSize: 15 }}>{route.name}</span>
                      <span style={{ fontSize: 11, background: "#1a1a1a", color: "#888", borderRadius: 6, padding: "2px 8px" }}>
                        {route.terrain === "flat" ? "🟢 Flat" : route.terrain === "moderate" ? "🟡 Moderate" : "🔴 Hilly"}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: "#888", marginBottom: 4 }}>{route.description}</div>
                    <div style={{ display: "flex", gap: 12, fontSize: 12 }}>
                      <span style={{ color: "#555" }}>📈 {route.elevationGain}</span>
                      <span style={{ color: "#4488cc" }}>☁ {route.weatherNote}</span>
                    </div>
                    {selectedRoute?.name === route.name && (
                      <div style={{ marginTop: 8, fontSize: 12, color: "#00ff88", fontWeight: 600 }}>✓ Selected — shown on map above</div>
                    )}
                  </div>
                ))}

                {routeOptions && (
                  <button
                    onClick={() => { setTab("run"); }}
                    style={{ width: "100%", padding: 12, background: "#00ff88", color: "#000", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer", marginTop: 4 }}
                  >
                    ▶ Start Run With This Route
                  </button>
                )}
              </>
            )}
          </>
        )}

        {tab === "training" && (
          <>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4, color: "#ccc" }}>Your Training Plan</div>
            {!isPro && (
              <div style={{ background: "#1a1500", border: "1px solid #3a3000", borderRadius: 10, padding: "12px 16px", marginBottom: 14 }}>
                {window.Capacitor?.isNativePlatform?.() ? (
                  <div>
                    <div style={{ fontSize: 13, color: "#aa8800", marginBottom: 8 }}>🔒 Pro feature — $9.99/month</div>
                    <button onClick={startCheckout} style={{ background: "#ffcc00", color: "#000", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", marginRight: 8 }}>
                      Upgrade Now
                    </button>
                    <button onClick={restorePurchases} style={{ background: "transparent", color: "#555", border: "1px solid #333", borderRadius: 8, padding: "8px 12px", fontSize: 12, cursor: "pointer" }}>
                      Restore
                    </button>
                    <div style={{ marginTop: 8, fontSize: 10, color: "#555" }}>
                      $9.99/month, auto-renews. <span onClick={() => { window.open("https://velocityai.run/terms.html","_blank"); }} style={{ color: "#00ff88", textDecoration: "underline", cursor: "pointer" }}>Terms</span> · <span onClick={() => { window.open("https://velocityai.run/privacy.html","_blank"); }} style={{ color: "#00ff88", textDecoration: "underline", cursor: "pointer" }}>Privacy</span>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, color: "#aa8800" }}>🔒 Pro feature</span>
                    <button onClick={startCheckout} style={{ background: "#ffcc00", color: "#000", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                      Upgrade $9.99/mo
                    </button>
                  </div>
                )}
              </div>
            )}
            {isPro && (
              <>
                {planLoading && (
                  <div style={{ textAlign: "center", color: "#555", padding: 40, fontSize: 14 }}>
                    Analyzing your runs and generating plan...
                  </div>
                )}
                {trainingPlan?.error && (
                  <div style={{ color: "#666", fontSize: 14, textAlign: "center", marginTop: 40 }}>
                    {trainingPlan.error}
                  </div>
                )}
                {trainingPlan?.goal && (
                  <>
                    <div style={{ background: "#0d1f0d", border: "1px solid #1a3a1a", borderRadius: 12, padding: 14, marginBottom: 14 }}>
                      <div style={{ fontSize: 11, color: "#00ff88", fontWeight: 700, marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>Coach Assessment</div>
                      <div style={{ fontSize: 14, color: "#cceecc", lineHeight: 1.6 }}>{trainingPlan.goal}</div>
                    </div>
                    {trainingPlan.days?.map((day) => (
                      <div key={day.day} style={{ background: "#111", borderRadius: 12, padding: 14, marginBottom: 8 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                          <span style={{ fontWeight: 700, color: day.type === "Rest" ? "#555" : "#00ff88", fontSize: 15 }}>{day.day}</span>
                          <span style={{ fontSize: 11, background: day.type === "Rest" ? "#1a1a1a" : "#0d200d", color: day.type === "Rest" ? "#444" : "#00cc66", borderRadius: 6, padding: "2px 8px", fontWeight: 600 }}>{day.type}</span>
                        </div>
                        <div style={{ fontSize: 14, color: "#ccc", marginBottom: 4 }}>{day.workout}</div>
                        <div style={{ fontSize: 12, color: "#555" }}>🎯 {day.goal}</div>
                      </div>
                    ))}
                    <button onClick={generateTrainingPlan} style={{ width: "100%", padding: 12, background: "none", border: "1px solid #333", color: "#555", borderRadius: 12, fontSize: 13, cursor: "pointer", marginTop: 4 }}>
                      ↻ Regenerate Plan
                    </button>
                  </>
                )}
              </>
            )}
          </>
        )}

        {tab === "history" && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#ccc" }}>Your Runs</div>

            </div>
            {runHistory.length === 0 ? (
              <div style={{ color: "#444", fontSize: 14, textAlign: "center", marginTop: 40 }}>
                No runs yet. Complete your first run to see history here.
              </div>
            ) : (
              runHistory.map((run) => (
                <div key={run.id} style={s.historyCard}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <span style={{ fontWeight: 700, color: "#00ff88" }}>{parseFloat(run.distance_miles).toFixed(2)} mi</span>
                    <span style={{ fontSize: 12, color: "#555" }}>{new Date(run.created_at).toLocaleDateString()}</span>
                  </div>
                  <div style={{ display: "flex", gap: 16, fontSize: 13, color: "#888" }}>
                    <span>⏱ {formatTime(run.duration_seconds)}</span>
                    <span>🏃 {run.avg_pace}/mi</span>
                    {run.calories && <span>🔥 {run.calories} cal</span>}
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>
      </>
      )}
    </div>
  );
}
